import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { FlightSegment, ItemPricing, Passenger, useUpdateTripItem } from "@/hooks/useTrips";
import { toast } from "sonner";
import { PriceInputSection } from "../PriceInputSection";
import { Plus, Trash2 } from "lucide-react";

const flightSchema = z.object({
  airline: z.string().min(1, "Airline is required"),
  flight_number: z.string().min(1, "Flight number is required"),
  // Departure
  departure_airport: z.string().min(1, "Departure airport is required"),
  departure_city: z.string().optional(),
  departure_time: z.string().min(1, "Departure time is required"),
  departure_terminal: z.string().optional(),
  departure_gate: z.string().optional(),
  // Arrival
  arrival_airport: z.string().min(1, "Arrival airport is required"),
  arrival_city: z.string().optional(),
  arrival_time: z.string().min(1, "Arrival time is required"),
  arrival_terminal: z.string().optional(),
  arrival_gate: z.string().optional(),
  // Flight details
  cabin_class: z.string().optional(),
  status: z.string().optional(),
  confirmation_number: z.string().optional(),
  aircraft: z.string().optional(),
  operated_by: z.string().optional(),
  stops: z.coerce.number().min(0).optional(),
  meals: z.string().optional(),
});

type FlightFormData = z.infer<typeof flightSchema>;

interface EditFlightDialogProps {
  flight: FlightSegment;
  tripId: string;
  index: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EditFlightDialog = ({
  flight,
  tripId,
  index,
  open,
  onOpenChange,
}: EditFlightDialogProps) => {
  const updateItem = useUpdateTripItem();
  const [pricing, setPricing] = useState<ItemPricing | undefined>(flight.pricing);

  // Initialize passengers, pre-filling confirmation_number from flight if not set per-passenger
  const [passengers, setPassengers] = useState<Passenger[]>(() => {
    const existingPassengers = flight.passengers || [];
    return existingPassengers.map(p => ({
      ...p,
      // Pre-fill with flight's main confirmation if passenger doesn't have their own
      confirmation_number: p.confirmation_number || flight.confirmation_number || "",
    }));
  });

  const form = useForm<FlightFormData>({
    resolver: zodResolver(flightSchema),
    defaultValues: {
      airline: flight.airline || "",
      flight_number: flight.flight_number || "",
      departure_airport: flight.departure_airport || "",
      departure_city: flight.departure_city || "",
      departure_time: flight.departure_time || "",
      departure_terminal: flight.departure_terminal || "",
      departure_gate: flight.departure_gate || "",
      arrival_airport: flight.arrival_airport || "",
      arrival_city: flight.arrival_city || "",
      arrival_time: flight.arrival_time || "",
      arrival_terminal: flight.arrival_terminal || "",
      arrival_gate: flight.arrival_gate || "",
      cabin_class: flight.cabin_class || "",
      status: flight.status || "",
      confirmation_number: flight.confirmation_number || "",
      aircraft: flight.aircraft || "",
      operated_by: flight.operated_by || "",
      stops: flight.stops ?? 0,
      meals: flight.meals || "",
    },
  });

  const addPassenger = () => {
    setPassengers([...passengers, { name: "", seat_number: "", ticket_number: "" }]);
  };

  const removePassenger = (idx: number) => {
    setPassengers(passengers.filter((_, i) => i !== idx));
  };

  const updatePassenger = (idx: number, field: keyof Passenger, value: string) => {
    const updated = [...passengers];
    updated[idx] = { ...updated[idx], [field]: value };
    setPassengers(updated);
  };

  const onSubmit = (data: FlightFormData) => {
    // Filter out empty passengers
    const validPassengers = passengers.filter(p => p.name.trim());

    // Preserve enrichment data when editing
    const updatedFlight: FlightSegment = {
      ...flight,
      ...data,
      pricing,
      passengers: validPassengers,
    };

    updateItem.mutate(
      {
        tripId,
        itemType: "flights",
        index,
        item: updatedFlight,
      },
      {
        onSuccess: () => {
          toast.success("Flight updated");
          onOpenChange(false);
        },
        onError: (error) => {
          toast.error(`Failed to update: ${error.message}`);
        },
      }
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Edit Flight</SheetTitle>
        </SheetHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 mt-6">
            {/* Flight Info Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground">Flight Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="airline"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Airline</FormLabel>
                      <FormControl>
                        <Input placeholder="United" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="flight_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Flight Number</FormLabel>
                      <FormControl>
                        <Input placeholder="UA123" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <FormControl>
                        <Input placeholder="Confirmed" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirmation_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirmation #</FormLabel>
                      <FormControl>
                        <Input placeholder="ABC123" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            {/* Departure Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground">Departure</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="departure_airport"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Airport Code</FormLabel>
                      <FormControl>
                        <Input placeholder="JFK" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="departure_city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City</FormLabel>
                      <FormControl>
                        <Input placeholder="New York" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="departure_time"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date & Time</FormLabel>
                    <FormControl>
                      <Input type="datetime-local" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="departure_terminal"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Terminal</FormLabel>
                      <FormControl>
                        <Input placeholder="4" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="departure_gate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Gate</FormLabel>
                      <FormControl>
                        <Input placeholder="B22" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            {/* Arrival Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground">Arrival</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="arrival_airport"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Airport Code</FormLabel>
                      <FormControl>
                        <Input placeholder="LAX" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="arrival_city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City</FormLabel>
                      <FormControl>
                        <Input placeholder="Los Angeles" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="arrival_time"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date & Time</FormLabel>
                    <FormControl>
                      <Input type="datetime-local" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="arrival_terminal"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Terminal</FormLabel>
                      <FormControl>
                        <Input placeholder="7" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="arrival_gate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Gate</FormLabel>
                      <FormControl>
                        <Input placeholder="A12" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            {/* Flight Details Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground">Flight Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="cabin_class"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cabin Class</FormLabel>
                      <FormControl>
                        <Input placeholder="Economy" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="aircraft"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Aircraft</FormLabel>
                      <FormControl>
                        <Input placeholder="Boeing 737-800" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="stops"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Stops</FormLabel>
                      <FormControl>
                        <Input type="number" min="0" placeholder="0" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="meals"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Meals</FormLabel>
                      <FormControl>
                        <Input placeholder="Snacks" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="operated_by"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Operated By</FormLabel>
                    <FormControl>
                      <Input placeholder="United Express" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Separator />

            {/* Passengers Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-muted-foreground">Passengers</h3>
                <Button type="button" variant="outline" size="sm" onClick={addPassenger}>
                  <Plus className="h-3 w-3 mr-1" />
                  Add
                </Button>
              </div>
              {passengers.length === 0 ? (
                <p className="text-sm text-muted-foreground">No passengers added</p>
              ) : (
                <div className="space-y-4">
                  {passengers.map((passenger, idx) => (
                    <div key={idx} className="flex gap-2 items-start p-3 border rounded-lg bg-muted/30">
                      <div className="flex-1 space-y-2">
                        <Input
                          placeholder="Passenger Name"
                          value={passenger.name}
                          onChange={(e) => updatePassenger(idx, "name", e.target.value)}
                        />
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          <Input
                            placeholder="Seat (e.g., 12A)"
                            value={passenger.seat_number || ""}
                            onChange={(e) => updatePassenger(idx, "seat_number", e.target.value)}
                          />
                          <Input
                            placeholder="Ticket #"
                            value={passenger.ticket_number || ""}
                            onChange={(e) => updatePassenger(idx, "ticket_number", e.target.value)}
                          />
                          <Input
                            placeholder="Confirmation #"
                            value={passenger.confirmation_number || ""}
                            onChange={(e) => updatePassenger(idx, "confirmation_number", e.target.value)}
                          />
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 text-muted-foreground hover:text-destructive"
                        onClick={() => removePassenger(idx)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Separator />

            {/* Pricing Section */}
            <PriceInputSection
              pricing={pricing}
              onChange={setPricing}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateItem.isPending}>
                {updateItem.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
};
